import React from 'react';
import { API_BASE_URL } from '../../../../config/constants';
import { getPublishedWorkflows } from './workflowStorage';

export const CHART_LINES = [
  { key: 'cachePercent', label: 'Cache %', color: '#6366f1', fillColor: 'rgba(99, 102, 241, 0.08)', format: v => `${v.toFixed(1)}%`, unit: '%', description: 'Stability' },
  { key: 'writeReadRatio', label: 'Write/Read Ratio', color: '#f59e0b', fillColor: 'rgba(245, 158, 11, 0.08)', format: v => `${v.toFixed(1)}%`, unit: '%', description: 'Thrashing' },
  { key: 'outputPerReq', label: 'Output/Request', color: '#10b981', fillColor: 'rgba(16, 185, 129, 0.08)', format: v => `${v.toFixed(0)} tok/req`, unit: 'tok/req', description: 'Productivity' },
  { key: 'requests', label: 'Requests', color: '#8b5cf6', fillColor: 'rgba(139, 92, 246, 0.08)', format: v => `${Math.round(v)} req`, unit: 'req', description: 'Volume' },
];

export const GRANULARITY_OPTIONS = [
  { value: 'day', label: 'Daily' },
  { value: 'hour', label: 'Hourly' },
  { value: 'minute', label: 'Minute' },
];

export function formatDateLabel(dateStr, granularity) {
  if (!dateStr) return '';
  if (granularity === 'minute' || granularity === 'hour') {
    const parts = dateStr.split('T');
    const d = new Date(parts[0] + 'T00:00:00');
    const dayLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return parts[1] ? `${dayLabel} ${parts[1]}` : dayLabel;
  }
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function deriveMetrics(raw) {
  return raw.map(d => ({
    ...d,
    cachePercent: d.cachePercent || 0,
    writeReadRatio: d.reads > 0 ? (d.writes / d.reads) * 100 : 0,
    outputPerReq: d.requests > 0 ? d.output / d.requests : 0,
  }));
}

export function ExperimentChart({ onClose }) {
  const [hoveredIdx, setHoveredIdx] = React.useState(null);
  const [hiddenLines, setHiddenLines] = React.useState({});
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [granularity, setGranularity] = React.useState('day');

  React.useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/usage-stats?granularity=${granularity}`)
      .then(r => r.json())
      .then(d => {
        let filtered = deriveMetrics(d);
        if (granularity === 'minute') {
          const cutoff = new Date(Date.now() - 60 * 60 * 1000).toISOString().slice(0, 16);
          filtered = filtered.filter(r => r.date >= cutoff);
        } else if (granularity === 'hour') {
          const cutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 13);
          filtered = filtered.filter(r => r.date >= cutoff);
        }
        setData(filtered);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [granularity]);

  const W = 700, H = 320, PAD = { top: 20, right: 20, bottom: 50, left: 10 };
  const cw = W - PAD.left - PAD.right;
  const ch = H - PAD.top - PAD.bottom;
  const n = data.length;

  const visibleLines = CHART_LINES.filter(l => !hiddenLines[l.key]);

  const maxes = {};
  CHART_LINES.forEach(l => {
    maxes[l.key] = Math.max(1, ...data.map(d => d[l.key] || 0));
  });

  function getY(key, val) {
    return PAD.top + ch - (val / maxes[key]) * ch;
  }

  function getX(i) {
    return PAD.left + (n > 1 ? (i / (n - 1)) * cw : cw / 2);
  }

  // Catmull-Rom to cubic bezier for smooth curves
  function makeSmoothPath(key) {
    if (n === 0) return '';
    if (n === 1) return `M${getX(0)},${getY(key, data[0][key] || 0)}`;

    const points = data.map((d, i) => ({ x: getX(i), y: getY(key, d[key] || 0) }));
    const tension = 0.3;
    let path = `M${points[0].x},${points[0].y}`;

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(0, i - 1)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(points.length - 1, i + 2)];

      const cp1x = p1.x + (p2.x - p0.x) * tension;
      const cp1y = p1.y + (p2.y - p0.y) * tension;
      const cp2x = p2.x - (p3.x - p1.x) * tension;
      const cp2y = p2.y - (p3.y - p1.y) * tension;

      path += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
    }
    return path;
  }

  // Create filled area path (same curve but closed to bottom)
  function makeAreaPath(key) {
    const linePath = makeSmoothPath(key);
    if (!linePath || n === 0) return '';

    const lastX = getX(n - 1);
    const firstX = getX(0);
    const bottom = PAD.top + ch;

    return `${linePath} L${lastX},${bottom} L${firstX},${bottom} Z`;
  }

  return (
    <div
      style={{
        width: '50vw',
        height: '100%',
        background: '#fff',
        borderLeft: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        flexShrink: 0,
        padding: '24px',
        paddingTop: '56px'
      }}
    >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#111827' }}>Performance</h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>Stability, thrashing, and productivity over time</p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#6b7280" style={{ width: '20px', height: '20px' }}>
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
            Loading usage data...
          </div>
        ) : error ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#ef4444', fontSize: '14px' }}>
            Failed to load: {error}
          </div>
        ) : data.length === 0 ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
            No usage data found
          </div>
        ) : (
          <>
            {/* Stats source note */}
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                Stats are gathered from local conversation history (~/.claude/projects/)
              </p>
            </div>

            {/* Legend + Granularity */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {CHART_LINES.map(l => (
                <button
                  key={l.key}
                  onClick={() => setHiddenLines(prev => ({ ...prev, [l.key]: !prev[l.key] }))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px',
                    borderRadius: '6px', fontSize: '12px', fontWeight: 500,
                    color: hiddenLines[l.key] ? '#9ca3af' : '#374151',
                    opacity: hiddenLines[l.key] ? 0.5 : 1,
                    transition: 'all 0.15s ease'
                  }}
                >
                  <span style={{
                    width: '10px', height: '10px', borderRadius: '50%',
                    background: hiddenLines[l.key] ? '#d1d5db' : l.color
                  }} />
                  {l.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '2px', background: '#f3f4f6', borderRadius: '6px', padding: '2px' }}>
              {GRANULARITY_OPTIONS.map(g => (
                <button
                  key={g.value}
                  onClick={() => setGranularity(g.value)}
                  style={{
                    padding: '4px 10px',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    background: granularity === g.value ? '#fff' : 'transparent',
                    color: granularity === g.value ? '#111827' : '#6b7280',
                    boxShadow: granularity === g.value ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                    transition: 'all 0.15s ease'
                  }}
                >
                  {g.label}
                </button>
              ))}
            </div>
            </div>

            {/* Chart */}
            <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map(pct => (
                <line
                  key={pct}
                  x1={PAD.left} x2={PAD.left + cw}
                  y1={PAD.top + ch * (1 - pct)} y2={PAD.top + ch * (1 - pct)}
                  stroke="#f3f4f6" strokeWidth="1"
                />
              ))}

              {/* Area fills */}
              {visibleLines.map(l => (
                <path
                  key={`fill-${l.key}`}
                  d={makeAreaPath(l.key)}
                  fill={l.fillColor}
                  stroke="none"
                />
              ))}

              {/* Smooth lines */}
              {visibleLines.map(l => (
                <path
                  key={l.key}
                  d={makeSmoothPath(l.key)}
                  fill="none"
                  stroke={l.color}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}

              {/* Dots - only show on hovered point */}
              {hoveredIdx !== null && visibleLines.map(l => (
                <circle
                  key={`${l.key}-${hoveredIdx}`}
                  cx={getX(hoveredIdx)}
                  cy={getY(l.key, data[hoveredIdx][l.key] || 0)}
                  r={4}
                  fill={l.color}
                  stroke="#fff"
                  strokeWidth="2"
                />
              ))}

              {/* Hover columns */}
              {data.map((d, i) => (
                <rect
                  key={i}
                  x={getX(i) - cw / n / 2} y={PAD.top}
                  width={cw / n} height={ch}
                  fill="transparent"
                  onMouseEnter={() => setHoveredIdx(i)}
                  onMouseLeave={() => setHoveredIdx(null)}
                  style={{ cursor: 'crosshair' }}
                />
              ))}

              {/* Hover line */}
              {hoveredIdx !== null && (
                <line
                  x1={getX(hoveredIdx)} x2={getX(hoveredIdx)}
                  y1={PAD.top} y2={PAD.top + ch}
                  stroke="#d1d5db" strokeWidth="1" strokeDasharray="4 3"
                />
              )}

              {/* X labels */}
              {data.map((d, i) => (
                <text
                  key={i}
                  x={getX(i)} y={H - 10}
                  textAnchor="middle" fontSize="10" fill="#9ca3af"
                  transform={`rotate(-45, ${getX(i)}, ${H - 10})`}
                >
                  {formatDateLabel(d.date, granularity)}
                </text>
              ))}

              {/* Published workflow annotation markers */}
              {(() => {
                const published = getPublishedWorkflows();
                if (published.length === 0 || data.length === 0) return null;
                const firstDate = data[0].date;
                const lastDate = data[data.length - 1].date;

                return published.map((w, wi) => {
                  const wDate = w.timestamp.split('T')[0];
                  if (wDate < firstDate.split('T')[0] || wDate > lastDate.split('T')[0]) return null;

                  let markerX = null;
                  for (let i = 0; i < data.length; i++) {
                    const dStr = data[i].date.split('T')[0];
                    if (dStr === wDate) { markerX = getX(i); break; }
                    if (dStr > wDate && i > 0) {
                      const prevD = new Date(data[i - 1].date.split('T')[0]);
                      const nextD = new Date(dStr);
                      const target = new Date(wDate);
                      const ratio = (target - prevD) / (nextD - prevD);
                      markerX = getX(i - 1) + (getX(i) - getX(i - 1)) * ratio;
                      break;
                    }
                  }
                  if (markerX === null) return null;

                  const label = w.name.length > 12 ? w.name.slice(0, 11) + '…' : w.name;
                  const labelW = Math.max(60, label.length * 6 + 16);

                  return (
                    <g key={w.id || wi}>
                      <line
                        x1={markerX} x2={markerX}
                        y1={PAD.top} y2={PAD.top + ch}
                        stroke="#9ca3af" strokeWidth="1" strokeDasharray="6 4"
                      />
                      <rect
                        x={markerX - labelW / 2} y={PAD.top - 16}
                        width={labelW} height="16" rx="4"
                        fill="#f3f4f6"
                      />
                      <text
                        x={markerX} y={PAD.top - 5}
                        textAnchor="middle" fontSize="9" fontWeight="600" fill="#6b7280"
                      >
                        {label}
                      </text>
                    </g>
                  );
                });
              })()}
            </svg>

            {/* Tooltip — always visible, shows hovered or latest day */}
            <div style={{
              marginTop: '12px', padding: '12px 16px',
              background: '#f9fafb', borderRadius: '8px',
              display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap',
              minHeight: '20px'
            }}>
              {(() => {
                const idx = hoveredIdx !== null ? hoveredIdx : data.length - 1;
                const d = data[idx];
                if (!d) return null;
                return (
                  <>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                      {formatDateLabel(d.date, granularity)}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {d.requests} requests
                    </span>
                    {visibleLines.map(l => (
                      <span key={l.key} style={{ fontSize: '12px', color: l.color, fontWeight: 500 }}>
                        {l.description}: {l.format(d[l.key] || 0)}
                      </span>
                    ))}
                  </>
                );
              })()}
            </div>
          </>
        )}
      </div>
  );
}
